// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiParameter,
    del,
    get,
    HasPagingParameters,
    patch,
    post,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import { BaseModel, PaginationModel } from "@insite/client-framework/Types/ApiModels";

const courseManagementUrl = "api/sainc/courses";
export interface CourseOrderHistoryModel {
    orderNumber?: string;
    orderDate?: Date;
    expirationDate?: Date;
    seatsPurchased?: number;
}

export interface CoursesModel extends BaseModel {
    id: string;
    courseName: string;
    courseNumber: string;
    seatsPurchased: number;
    seatsAvailable: number;
    seatsExpired: number;
    isBundle: boolean;
    courseOrderHistories: CourseOrderHistoryModel[];
}

export interface CoursesCollectionModel extends BaseModel {
    courses: CoursesModel[] | null;
    pagination: PaginationModel | null;
}

export interface CourseManagementModel extends BaseModel {
    courseId: string;
    id: string;
    firstName: string;
    lastName: string;
    registrationDate: string;
    customerNumber: string;
    company?: string;
    email: string;
    status: string;
    reclaimed?: boolean;
    reclaimedOn?: string;
    percentageCompleted?: number;
}

export interface CourseManagementCollectionModel extends BaseModel {
    courseRegistrations: CourseManagementModel[] | null;
    pagination: PaginationModel | null;
}

export interface RegisterUserApiParameter extends ApiParameter {
    firstName: string;
    lastName: string;
    company?: string;
    email: string;
    courseId: string;
}

export interface GetRegisteredUsersApiParameter extends ApiParameter, HasPagingParameters {
    sortBy?: string;
    courseId: string;
}

export interface GetCoursesApiParameter extends ApiParameter, HasPagingParameters {
    sortBy?: string;
}

export interface UpdateRegisteredUserApiParameter extends ApiParameter {
    firstName: string;
    lastName: string;
    company: string;
    courseId: string;
    registrationId: string;
}

export interface DeleteRegisteredUserApiParameter extends ApiParameter {
    courseId: string;
    registrationId: string;
}

export async function getCourses(parameter: GetCoursesApiParameter): Promise<ServiceResult<CoursesCollectionModel>> {
    try {
        const result = await get<CoursesCollectionModel>(courseManagementUrl, parameter);
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function registerUser(parameter: RegisterUserApiParameter): Promise<ServiceResult<CourseManagementModel>> {
    try {
        const result = await post<CourseManagementModel>(
            `${courseManagementUrl}/${parameter.courseId}/registrations`,
            parameter as CourseManagementModel,
        );
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function getRegisteredUsers(
    parameter: GetRegisteredUsersApiParameter,
): Promise<ServiceResult<CourseManagementCollectionModel>> {
    try {
        const result = await get<CourseManagementCollectionModel>(
            `${courseManagementUrl}/${parameter.courseId}/registrations`,
            parameter,
        );
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function updateRegisteredUser(
    parameter: UpdateRegisteredUserApiParameter,
): Promise<ServiceResult<CourseManagementModel>> {
    try {
        const result = await patch<CourseManagementModel>(
            `${courseManagementUrl}/${parameter.courseId}/registrations/${parameter.registrationId}`,
            parameter,
        );
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}

export async function deleteRegisteredUser(parameter: DeleteRegisteredUserApiParameter): Promise<ServiceResult<void>> {
    try {
        const result = await del(
            `${courseManagementUrl}/${parameter.courseId}/registrations/${parameter.registrationId}`,
        );
        return {
            successful: true,
            result,
        };
    } catch (error) {
        return {
            successful: false,
            errorMessage: (error as any)?.errorJson?.message,
        };
    }
}
